import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { ConditionalAccess } from '../../../../admin/components/ConditionalAccess';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { ILocalNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { copyToClipboard, getSocialMediaPlatformLogoUrl, sortPlatformTargets } from '../../../../models/UiUtils';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { brandPrimaryHover } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { LocalNotificationObserver } from '../../LocalNotificationObserver';
import { CancelIconFill } from '../../svgs/icons/CancelIconFill';
import { ClipboardIcon } from '../../svgs/icons/ClipboardIcon';
import { DangerIcon } from '../../svgs/icons/DangerIcon';
import { InfoIcon } from '../../svgs/icons/InfoIcon';
import { PopoverType, TinyPopover } from '../../TinyPopover';
import { SocialMediaPostTargetStatusIndicators } from '../SocialMediaPostTargetStatusIndicators';
import { styleSheet } from './styles';

export interface ISocialMediaReportingTableRowProps {
	report: Api.SocialMediaPostReportViewModel;
	onTitleClick?: (report: Api.SocialMediaPostReportViewModel) => void;
	impersonationContext?: Api.IImpersonationContext;
}

export const SocialMediaReportingTableRow = observer(
	({ report, onTitleClick, impersonationContext }: ISocialMediaReportingTableRowProps) => {
		const { logInput, logApiError } = useEventLogging('SocialMediaPostReconnectionAttempt');
		const [isOpen, , setIsOpen] = useLambda(false);
		const userSession = useUserSession();
		const [showFailedPopover, , setShowFailedPopover] = useLambda(false);

		const getStatusStyle = (status: Api.PostStatus) => {
			return (styleSheet as any)[`status${status}`] as StyleDeclarationValue;
		};

		const onReconnectLinkClicked = React.useCallback(() => {
			const { origin, pathname } = window.location;
			window?.open(`${origin}${pathname}#/settings/social-media/social-media`, '_blank');
			logInput('ReconnectAttemptLink', 'Click');
		}, [logInput]);

		const generatePostUrl = async (target: Api.IPostTarget) => {
			const opResult = await userSession.webServiceHelper.callWebServiceAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						provider: target.provider,
					},
					urlPath: `social/post/${report.id}/generateProviderLink`,
				}),
				'POST',

				target.state.lastConnectionState
			);
			if (opResult.success) {
				window?.open(opResult.value, '_blank');
			}
		};

		const getDateToDisplay = (statusDefault?: boolean) => {
			switch (!statusDefault && report.status) {
				case Api.PostStatus.Succeeded:
					return moment(report?.completedDate || report.dueDate).format('MM/DD/YYYY');
				case Api.PostStatus.Cancelled:
					return moment(report?.cancelledDate || report.lastModifiedDate).format('MM/DD/YYYY');
				default:
					return moment(report.dueDate).format('MM/DD/YYYY');
			}
		};

		const getTimeToDisplay = (statusDefault?: boolean) => {
			switch (!statusDefault && report.status) {
				case Api.PostStatus.Succeeded:
					return moment(report?.completedDate || report.dueDate).format('h:mma');
				case Api.PostStatus.Cancelled:
					return moment(report?.cancelledDate || report.lastModifiedDate).format('h:mma');
				default:
					return moment(report.dueDate).format('h:mma');
			}
		};

		const onEditSocialPost = async (notification: ILocalNotification<Api.SocialMediaPostViewModel>) => {
			if (notification.info?.id === report.id) {
				try {
					await report.load();
				} catch (error) {
					logApiError('SocialMediaPostReportLoad-Error', error);
				}
			}
		};

		const displayTargets = React.useMemo(() => {
			if (report?.designatedTargets?.length > 0) {
				return sortPlatformTargets(report?.designatedTargets);
			}

			return sortPlatformTargets(report?.targets) || [];
		}, [report?.designatedTargets, report?.targets]);

		// to asssign a reconnect indicator verify that the post is still in process and check that either new or old targets have a disconnected target
		const isReconnect =
			(report.status === Api.PostStatus.Scheduled || report.status === Api.PostStatus.Pending) &&
			displayTargets?.find(
				x =>
					x.state?.lastConnectionState?.state !== Api.SocialMediaConnectionState.Connected ||
					!x.state?.lastConnectionState?.postTargetId
			);

		const handleCopyId = (ev: React.MouseEvent<HTMLElement>) => {
			ev.stopPropagation();
			copyToClipboard(report.id ?? '');

			toast.success('ID copied to clipboard');
		};

		const handleViewPost = (postTarget: Api.IPostTarget) => () => generatePostUrl(postTarget);

		return (
			<li className={css(styleSheet.row)}>
				<div className={css(styleSheet.nameColumn, styleSheet.nameColumnRow)}>
					<div className={css(styleSheet.rowName)}>
						<div className={css(styleSheet.rowTitle)}>
							<button
								className={css(baseStyleSheet.brandLink, baseStyleSheet.truncateText)}
								onClick={onTitleClick != null ? () => onTitleClick(report) : undefined}
							>
								<div className={css(baseStyleSheet.truncateText)}>{report.name || 'Custom Name'}</div>
							</button>
							<ConditionalAccess permission={AdminPermission.ReadAccount}>
								<span onClick={handleCopyId} className={css(styleSheet.rowClipboardParent)}>
									<ClipboardIcon />
								</span>
							</ConditionalAccess>
						</div>
						<div className={css(styleSheet.rowDetails)}>
							<div className={css(styleSheet.rowDetailsRow)}>
								<div className={css(styleSheet.rowDetailsStatus, getStatusStyle(report.status))}>
									{report.status === Api.PostStatus.PartiallySucceeded
										? 'Partial Success'
										: report.status === Api.PostStatus.Succeeded
											? 'Posted'
											: report.status}
								</div>
								&nbsp;
								{report.status === Api.PostStatus.Scheduled || report.status === Api.PostStatus.Pending ? 'for ' : 'on'}
								<span>
									&nbsp;
									<span className={css(baseStyleSheet.fontBold, styleSheet.dateText)}>{getDateToDisplay()}</span>
									{' at'}
									&nbsp;
									{getTimeToDisplay()}
								</span>
							</div>
							<div className={css(styleSheet.rowDetailsRow)}>
								<div className={css(baseStyleSheet.truncateText)}>
									{`by ${report.creator?.firstName} ${report.creator?.lastName}`}
								</div>
							</div>
							{(((report.status === Api.PostStatus.Scheduled || report.status === Api.PostStatus.Pending) &&
								displayTargets?.find(
									x =>
										x.state?.lastConnectionState?.state !== Api.SocialMediaConnectionState.Connected ||
										!x.state?.lastConnectionState?.postTargetId
								)) ||
								report.status === Api.PostStatus.Cancelled) && (
								<div className={css(styleSheet.popover)}>
									<TinyPopover
										anchor={
											<div
												className={css(
													baseStyleSheet.flex,
													baseStyleSheet.itemsCenter,
													styleSheet.detailed,
													isReconnect && styleSheet.reconnect
												)}
												onMouseEnter={setIsOpen(true)}
												onMouseLeave={setIsOpen(false)}
											>
												{isReconnect ? (
													<DangerIcon />
												) : (
													<InfoIcon className={css(styleSheet.icon)} fillColor={brandPrimaryHover} />
												)}
												{isReconnect ? 'Action Needed' : 'Campaign Status'}
											</div>
										}
										anchorStyles={[styleSheet.detailed]}
										dismissOnOutsideAction={true}
										autoCloseOtherPopoversOnHover={true}
										type={isReconnect ? PopoverType.errorSecondary : PopoverType.background}
										isOpen={isOpen}
										placement={['right', 'bottom', 'top']}
										align='center'
									>
										<p className={css(styleSheet.popoverContent)}>
											{isReconnect
												? 'One or more of the post targets is not connected.'
												: `This campaign was scheduled for ${getDateToDisplay(true)} at ${getTimeToDisplay(true)}.`}
										</p>
									</TinyPopover>
									{isReconnect && report?.canEdit && (
										<button
											className={css(baseStyleSheet.ctaButton, styleSheet.reconnectButton)}
											onClick={onReconnectLinkClicked}
										>
											Reconnect
										</button>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className={css(styleSheet.createdColumn, styleSheet.rowResponses)}>
					{report.creationDate ? format(new Date(report.creationDate), 'MM/dd/yyyy') : 'N/A'}
				</div>
				<div className={css(styleSheet.platformColumn, styleSheet.rowResponses)}>
					<div className={css(styleSheet.platformAccounts)}>
						{displayTargets && displayTargets.filter(x => x.state?.lastConnectionState).length > 0 ? (
							displayTargets?.map((x: Api.IPostTarget, i: number) =>
								x.state?.lastConnectionState ? (
									<div className={css(styleSheet.platformIconRow)} key={i}>
										<img src={getSocialMediaPlatformLogoUrl(x.provider)} />
										<div className={css(styleSheet.connectionStatus, styleSheet.platformText)}>
											{x.state?.lastConnectionState?.userName}
											{x.state?.lastConnectionState && ' /'}{' '}
											{x.state?.lastConnectionState?.postTargetDisplayName || x.provider}
											<SocialMediaPostTargetStatusIndicators
												postStatus={report.status}
												postTarget={x}
												showTargetProvider={true}
											/>
										</div>
									</div>
								) : null
							)
						) : (
							<div className={css(styleSheet.connectionStatus, styleSheet.platformText)}>
								{' '}
								Originally scheduled for {Array.from(new Set(displayTargets.map(x => x.provider))).join(', ')}
								<br />
								<TinyPopover
									align='end'
									anchor={
										<div
											onMouseEnter={setShowFailedPopover(true)}
											onMouseLeave={setShowFailedPopover(false)}
											className={css(baseStyleSheet.flex)}
										>
											<CancelIconFill />
										</div>
									}
									styles={[styleSheet.popoverMargin]}
									dismissOnOutsideAction={true}
									isOpen={showFailedPopover}
									placement={['right', 'top', 'left', 'bottom']}
									type={PopoverType.errorSecondary}
								>
									<div className={css(styleSheet.popoverWarning)}>No account info present. Please reconnect.</div>
								</TinyPopover>
							</div>
						)}
					</div>
				</div>
				<div className={css(styleSheet.viewPostColumn, styleSheet.rowResponses)}>
					<div className={css(styleSheet.platformAccounts)}>
						{displayTargets?.map((x: Api.IPostTarget, i: number) => (
							<div className={css(styleSheet.platformIconRow)} key={i}>
								<img src={getSocialMediaPlatformLogoUrl(x.provider)} />
								<div className={css(styleSheet.connectionStatus)}>
									{x.state?.status === Api.PostTargetStatus.Posted ? (
										<button
											className={css(baseStyleSheet.brandLink, styleSheet.platformText)}
											onClick={handleViewPost(x)}
										>
											View Post
										</button>
									) : (
										<>Not Available</>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
				<LocalNotificationObserver topic={Topics.EDIT_SOCIAL_POST} onNotificationReceived={onEditSocialPost} />
			</li>
		);
	}
);
